import React, { useContext, useEffect, useState } from "react";
import RichTextEditor from './RichTextEditor'; 
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, TextField, Grid, MenuItem } from "@mui/material";
import { AuthContext } from "../context/AuthProvider";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const INITIAL_VALUES = {
  countryId: "",
  country: "",
  region: "",
  vintage: "",
  score: "",
  drink: "",
  description: "",
  url: "",
};

const Input = (props) => (
  <TextField variant="outlined" color="primary" fullWidth {...props} />
);

export default function AddChartwineDialog({ open, onClose, onSave, rowId }) {
  const [data, setData] = useState(INITIAL_VALUES);
  const [form, setForm] = useState({ description: "" });
  const [countries, setCountries] = useState([]);
  const authContext = useContext(AuthContext);

  const handleSave = () => {
    onSave({ ...data, description: form.description });
    setData(INITIAL_VALUES);
    setForm({ description: "" });
  };

  const handleClose = () => {
    setData(INITIAL_VALUES);
    setForm({ description: "" });
    onClose();
  };

  const isValid = () => {
    if (
      !data.countryId ||
      !data.vintage ||
      !data.score ||
      !data.drink ||
      !form.description ||
      !data.url
    ) {
      return false;
    }
    if (
      isNaN(Number(data.vintage)) ||
      isNaN(Number(data.score)) ||
      Number(data.vintage) <= 0 ||
      Number(data.score) <= 0
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    const findById = async (id) => {
      const response = await fetchWithInterceptor(
        `/api/vintage-chart/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setData({ ...data, countryId: data.countryId || "" });
        setForm({ description: data.description || "" });
      }
    };
    if (rowId) {
      findById(rowId);
    } else {
      setData(INITIAL_VALUES);
    }
  }, [rowId, authContext]);

  useEffect(() => {
    const fetchCountries = async () => {
      const res = await fetchWithInterceptor("/api/vintage-chart/countries", {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setCountries(data);
      } else {
        console.error("Error fetching countries");
      }
    };

    fetchCountries();
  }, [authContext]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      aria-labelledby="max-width-dialog-title"
    >
      <DialogTitle id="max-width-dialog-title">{rowId ? 'Edit' : 'Create'} Vintage Chart</DialogTitle>
      <DialogContent width="lg">
        <DialogContentText>
          Fill out the following information about the vintage chart.
        </DialogContentText>
        <form noValidate autoComplete="off" style={{ paddingTop: 16 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                label="Country"
                value={data.countryId}
                onChange={(e) =>
                  setData({ ...data, countryId: parseInt(e.target.value) })
                }
                variant="outlined"
                fullWidth
                required
                size="medium"
                sx={{
                  '& .MuiSelect-nativeInput': {
                    position: 'static',
                  }
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Input
                value={data.region}
                onChange={(e) => setData({ ...data, region: e.target.value })}
                label="Region"
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Input
                value={data.vintage}
                onChange={(e) => setData({ ...data, vintage: e.target.value })}
                label="Vintage"
                required
                inputProps={{ maxLength: 4, minLength: 4 }}
                error={!!data.vintage && isNaN(Number(data.vintage))}
                helperText={
                  !!data.vintage && isNaN(Number(data.vintage))
                    ? "Enter a valid year"
                    : ""
                }
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Input
                value={data.score}
                onChange={(e) => setData({ ...data, score: e.target.value })}
                label="Score"
                required
                error={!!data.score && isNaN(Number(data.score))}
                helperText={
                  !!data.score && isNaN(Number(data.score))
                    ? "Enter a valid score"
                    : ""
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 16 }}>
            <Grid size={{ xs: 6 }}>
              <Input
                value={data.drink}
                onChange={(e) => setData({ ...data, drink: e.target.value })}
                label="Recommendation"
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Input
                value={data.url}
                onChange={(e) => setData({ ...data, url: e.target.value })}
                label="URL Link"
                name="url"
                required
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: 16 }}>
            <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
              <Typography variant="body2" gutterBottom>Description*</Typography>
              <div
                style={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 4,
                  padding: '16.5px 14px',
                }}
              >
                <RichTextEditor
                  value={form.description}
                  onChange={(val) => setForm({ ...form, description: val })}
                />
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!isValid()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}